/* eslint-disable sonarjs/cognitive-complexity */
import type { HeaderPromotion } from '@/components/_headers/headerData'
import type {
  CosmicProductType,
  CosmicVariationType,
} from '@/lib/cms/cosmic/types/CosmicProductType'
import { useGTM } from '@/lib/hooks/google-analytics/useGTM'
import useClientRedirect from '@/lib/hooks/routing/useRedirect'
import { mixpanelService } from '@/lib/services/mixpanel/initializeMixPanel'
import { MixpanelEvent } from '@/lib/services/mixpanel/mixpanelEvent'
import { trackShoSelectProduct } from '@/lib/services/mixpanel/events/productEvents'
import useGlobalStore from '@/store/useGlobalStore'
import Image from 'next/image'

import { useEffect, useState } from 'react'

// This component will gather all the promotions from all the header items, and display them
// in a swipeable carousel. This will be displayed in the MobileDrawer component.

export const MobileDrawerPromotions = () => {
  const {
    cms: { header },
    setApp,
  } = useGlobalStore()
  const { clientRedirect } = useClientRedirect()
  const { gtm } = useGTM()

  const [promotions, setPromotions] = useState<HeaderPromotion[] | null>(null)

  useEffect(() => {
    if (!header) return
    if (header === 'error') return
    if (header === 'loading') return

    const globalPromotions = header.items
      ? header.items.flatMap((item) => {
          if (!item || !item.headerItem) return []

          const { content } = item.headerItem

          if (!content) return []

          const promos = []

          if (
            content.rightSection &&
            content.rightSection.promotion &&
            content.rightSection.promotion.title &&
            content.rightSection.promotion.description &&
            content.rightSection.promotion.image
          ) {
            promos.push(content.rightSection.promotion)
          }
          if (
            content.leftSection &&
            content.leftSection.promotion &&
            content.leftSection.promotion.title &&
            content.leftSection.promotion.description &&
            content.leftSection.promotion.image
          ) {
            promos.push(content.leftSection.promotion)
          }

          return promos
        })
      : null

    setPromotions(globalPromotions)
  }, [header])

  if (!promotions || !promotions[0]) return null

  const promotion = promotions[0]

  return (
    <button
      type="button"
      className="cursor-pointer no-underline"
      onClick={async () => {
        gtm.navbarClicked({
          action: promotion.title,
          ads_title: promotion.button.title,
          ads_image_name: promotion.description,
          ads_url: promotion.button.href,
        })

        mixpanelService
          .trackEvent(MixpanelEvent.NAVIGATION_BAR_CLICKED, {
            action: promotion.title,
            ads_title: promotion.button.title,
            ads_image_name: promotion.description,
            ads_url: promotion.button.href,
          })
          .catch(() => {})

        gtm.navbarMenuItemClicked({
          action: 'ads_learn_more',
          category: 'ads',
          ads_title: promotion.title,
          ads_image_name: promotion.image,
          ads_url: promotion.button.href,
        })

        await mixpanelService
          .trackEvent(MixpanelEvent.NAVIGATION_BAR_SHOP_MENU_ITEM_CLICKED, {
            action: 'ads_learn_more',
            category: 'ads',
            ads_title: promotion.title,
            ads_image_name: promotion.image,
            ads_url: promotion.button.href,
          })
          .catch(() => {})

        const urlParts = promotion.button.href.split('/')
        const productSlug = urlParts.at(-1)?.split('?')[0] || ''
        const sku = promotion.button.href.includes('?sku=')
          ? promotion.button.href.split('?sku=')[1]
          : ''
        const productName = productSlug.toUpperCase()
        const productForTracking: CosmicProductType = {
          name: productName,
          slug: productSlug,
          description: promotion.description,
          variations: [
            {
              sku,
              combinationName:
                productSlug.charAt(0).toUpperCase() + productSlug.slice(1),
              pricing: null,
              isDefault: true,
              isDisable: false,
              partsLabel: '',
            } as CosmicVariationType,
          ],
          attributes: [],
          availableFor: {
            customer: true,
            member: true,
            associate: true,
            employee: true,
          },
        }

        await trackShoSelectProduct({
          product: productForTracking,
          selectedVariation: productForTracking.variations[0],
          eventLocation: 'navigation_bar',
          action: 'navigation_bar_shop_menu_shop_now_clicked',
          customerType: 'Customer',
          currencyCode: 'USD',
        }).catch((error) => {
          console.error('Product link tracking failed (mobile):', error)
        })

        await clientRedirect.to({ path: promotion.button.href }).catch(() => {})
        setApp({ isMobileDrawerOpen: false })
      }}
    >
      <div className="flex items-start rounded-lg bg-slate-100 p-3">
        <Image
          className="rounded-lg"
          height={120}
          width={120}
          alt="Promotion"
          src={promotion.image}
        />

        <div className="ml-3 flex flex-col justify-center gap-3 text-left">
          <span className="bold text-md font-bold text-primary underline">
            {promotion.title}
          </span>
          <span className="text-sm text-primary">{promotion.description}</span>
        </div>
      </div>
    </button>
  )
}
